import React, { useEffect, useRef, useState } from 'react';
import { MapPin, Loader2, Maximize2, X } from 'lucide-react';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

/**
 * DayMapPanel — Google Maps view of a single day's jobs.
 *
 * Props:
 *  - jobs: array of jobs for the selected day (must have .latitude / .longitude)
 *  - companyLogo: optional URL — used as the marker icon when present
 *  - isWorker: hides price in info window when true
 *  - getStaffColor: optional fn(assignee) -> color
 *  - dayLabel: e.g. "Thu 7 May" — shown in the info bar
 *  - dataTestId
 *
 * Behaviour mirrors DayPilot:
 *  - Skipped jobs are excluded from markers.
 *  - Completed jobs render in green; scheduled use staff colour.
 *  - Numbered markers in route order.
 *  - Toggle (Show Map / Hide Map), fullscreen, "no locations" empty state.
 */
const DayMapPanel = ({
  jobs = [],
  companyLogo = null,
  isWorker = false,
  getStaffColor,
  dayLabel = '',
  dataTestId = 'day-map-panel',
}) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const workerMarkersRef = useRef({});  // keyed by worker_id
  const infoWindowRef = useRef(null);

  const [mapLoaded, setMapLoaded] = useState(false);
  const [collapsed, setCollapsed] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [geocodingMsg, setGeocodingMsg] = useState('');
  const [workerLocations, setWorkerLocations] = useState([]);  // /api/team/locations response
  
  // Poll live worker locations every 30s while map is open. Endpoint returns
  // owner's pin too (if they're tracking themselves), so this also surfaces
  // the current user on the WorkPlanner map.
  useEffect(() => {
    if (collapsed) return undefined;
    let cancelled = false;
    const fetchLocations = async () => {
      try {
        const token = localStorage.getItem('streakfree_token');
        if (!token) return;
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/team/locations`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setWorkerLocations(Array.isArray(data?.locations) ? data.locations : []);
      } catch (_) { /* silent */ }
    };
    fetchLocations();
    const id = setInterval(fetchLocations, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [collapsed]);

  // Listen for global "collapse" events fired when modals open (Done / Charge / Skip)
  // so the map auto-tucks away and the bottom sheet has the full screen to itself.
  useEffect(() => {
    const handler = () => {
      setCollapsed(true);
      setFullscreen(false);
    };
    window.addEventListener('streakfree:collapse-day-map', handler);
    return () => window.removeEventListener('streakfree:collapse-day-map', handler);
  }, []);

  // Only show pins for jobs still on the route — exclude skipped AND completed.
  // As workers tick jobs off, the map clears down so the remaining stops are
  // the only ones still on the route.
  const validJobs = jobs.filter(
    (j) =>
      j.latitude &&
      j.longitude &&
      j.status !== 'skipped' &&
      j.status !== 'done' &&
      j.status !== 'completed'
  );

  // Load Google Maps SDK once
  useEffect(() => {
    if (window.google?.maps) {
      setMapLoaded(true);
      return;
    }
    if (document.querySelector('script[src*="maps.googleapis.com"]')) {
      const tick = setInterval(() => {
        if (window.google?.maps) {
          setMapLoaded(true);
          clearInterval(tick);
        }
      }, 100);
      return () => clearInterval(tick);
    }
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => setMapLoaded(true);
    document.head.appendChild(script);
  }, []);

  // Render markers
  useEffect(() => {
    if (!mapLoaded || !mapRef.current || !window.google?.maps) return;
    if (collapsed && !fullscreen) return; // don't render if not visible

    if (!mapInstanceRef.current) {
      mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
        center: { lat: 50.95, lng: -0.13 },
        zoom: 12,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        styles: [
          { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
        ],
      });
      infoWindowRef.current = new window.google.maps.InfoWindow();
    }

    // Clear old markers
    markersRef.current.forEach((m) => {
      if (m.setMap) m.setMap(null);
      else if (m.map !== undefined) m.map = null;
    });
    markersRef.current = [];

    const bounds = new window.google.maps.LatLngBounds();

    validJobs.forEach((job, idx) => {
      const isCompleted = job.status === 'done' || job.status === 'completed';
      const staffColor =
        (getStaffColor && getStaffColor(job.assignee)) || '#3b82f6';
      const markerColor = isCompleted ? '#10b981' : staffColor;

      let marker = null;

      if (companyLogo) {
        const content = document.createElement('div');
        content.innerHTML = `
          <div style="position: relative; width: 44px; height: 44px; cursor: pointer;">
            <div style="width: 40px; height: 40px; border-radius: 50%; border: 4px solid ${markerColor}; overflow: hidden; background: white; box-shadow: 0 2px 8px rgba(0,0,0,0.3);">
              <img src="${companyLogo}" style="width: 100%; height: 100%; object-fit: cover;" />
            </div>
            <div style="position: absolute; bottom: -2px; right: -2px; width: 18px; height: 18px; border-radius: 50%; background: ${markerColor}; border: 2px solid white; display: flex; align-items: center; justify-content: center; color: white; font-size: 10px; font-weight: bold;">${idx + 1}</div>
          </div>
        `;
        const Advanced = window.google.maps.marker?.AdvancedMarkerElement;
        if (Advanced) {
          marker = new Advanced({
            position: { lat: job.latitude, lng: job.longitude },
            map: mapInstanceRef.current,
            content,
          });
        }
      }
      if (!marker) {
        marker = new window.google.maps.Marker({
          position: { lat: job.latitude, lng: job.longitude },
          map: mapInstanceRef.current,
          label: {
            text: String(idx + 1),
            color: 'white',
            fontWeight: 'bold',
            fontSize: '12px',
          },
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 16,
            fillColor: markerColor,
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 3,
          },
        });
      }

      const handleClick = () => {
        infoWindowRef.current.setContent(`
          <div style="min-width: 200px; font-family: system-ui; padding: 4px;">
            <div style="font-weight: 600; font-size: 14px;">${job.customer_name || job.customerName || 'Unknown'}</div>
            <div style="font-size: 12px; color: #666; margin: 4px 0;">${job.customer_address || job.address || ''}</div>
            <div style="display: flex; justify-content: space-between; padding-top: 8px; border-top: 1px solid #eee;">
              ${!isWorker ? `<span style="font-weight: 600; color: #10b981;">£${(job.price || 0).toFixed(2)}</span>` : '<span></span>'}
              <span style="font-size: 11px; padding: 2px 8px; background: ${isCompleted ? '#dcfce7' : '#dbeafe'}; color: ${isCompleted ? '#16a34a' : '#2563eb'}; border-radius: 9999px;">
                ${isCompleted ? 'Done' : 'To Do'}
              </span>
            </div>
          </div>
        `);
        infoWindowRef.current.open(mapInstanceRef.current, marker);
      };

      if (marker.addListener) marker.addListener('click', handleClick);
      else if (marker.content) marker.content.addEventListener('click', handleClick);

      markersRef.current.push(marker);
      bounds.extend({ lat: job.latitude, lng: job.longitude });
    });

    if (validJobs.length > 0) {
      mapInstanceRef.current.fitBounds(bounds, 50);
    }
    
    // Force a resize after layout settle so the map paints correctly when expanding
    setTimeout(() => {
      if (mapInstanceRef.current && window.google?.maps) {
        window.google.maps.event.trigger(mapInstanceRef.current, 'resize');
        if (validJobs.length > 0) {
          mapInstanceRef.current.fitBounds(bounds, 50);
        }
      }
    }, 200);
  }, [mapLoaded, collapsed, fullscreen, validJobs, companyLogo, isWorker, getStaffColor]);
  
  // ─── Worker pins (separate effect so job markers don't recreate on each
  // workerLocations poll — that's what caused the constantly-pulsing /
  // loading flicker the user reported). ─────────────────────────────────
  useEffect(() => {
    if (!mapLoaded || !mapInstanceRef.current || !window.google?.maps) return;
    
    const seenWorkerIds = new Set();
    workerLocations.forEach((loc) => {
      if (!loc?.lat || !loc?.lng) return;
      const wid = loc.worker_id || loc.user_id || loc.worker_name;
      if (!wid) return;
      seenWorkerIds.add(wid);
      
      const pos = { lat: loc.lat, lng: loc.lng };
      const existing = workerMarkersRef.current[wid];
      
      if (existing) {
        if (typeof existing.setPosition === 'function') existing.setPosition(pos);
        else existing.position = pos;
      } else {
        const workerMarker = new window.google.maps.Marker({
          position: pos,
          map: mapInstanceRef.current,
          title: `${loc.worker_name || 'Worker'} (live)`,
          zIndex: 9999,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 9,
            fillColor: '#2563eb',
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 3,
          },
        });
        workerMarker.addListener('click', () => {
          infoWindowRef.current.setContent(`
            <div style="min-width: 180px; font-family: system-ui; padding: 4px;">
              <div style="font-weight: 600; font-size: 14px;">${loc.worker_name || 'Worker'} (live)</div>
              <div style="font-size: 11px; color: #666; margin-top: 4px;">${loc.updated_at ? new Date(loc.updated_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : ''}</div>
            </div>
          `);
          infoWindowRef.current.open(mapInstanceRef.current, workerMarker);
        });
        workerMarkersRef.current[wid] = workerMarker;
      }
    });
    
    // Clean up disappeared workers
    Object.keys(workerMarkersRef.current).forEach((wid) => {
      if (!seenWorkerIds.has(wid)) {
        const m = workerMarkersRef.current[wid];
        if (m?.setMap) m.setMap(null);
        delete workerMarkersRef.current[wid];
      }
    });
  }, [mapLoaded, workerLocations]);

  const totalCount = jobs.length;
  const doneCount = jobs.filter((j) => j.status === 'done' || j.status === 'completed').length;

  return (
    <div data-testid={dataTestId}>
      {/* Map container */}
      <div
        className={`relative bg-zinc-200 transition-all duration-300 overflow-hidden ${
          fullscreen
            ? 'fixed inset-0 z-50 h-screen'
            : collapsed
            ? 'h-0'
            : 'h-56 sm:h-72'
        }`}
      >
        <div
          ref={mapRef}
          className={`w-full ${fullscreen ? 'h-full' : 'h-56 sm:h-72'}`}
        />

        {!mapLoaded && (!collapsed || fullscreen) && (
          <div className="absolute inset-0 flex items-center justify-center bg-zinc-200">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
          </div>
        )}

        {fullscreen && (
          <button
            type="button"
            onClick={() => setFullscreen(false)}
            className="absolute top-4 left-4 z-[1000] bg-zinc-800 text-white p-3 rounded-xl shadow-lg hover:bg-zinc-700 transition-colors"
            style={{ top: 'max(env(safe-area-inset-top, 16px), 16px)' }}
            aria-label="Close fullscreen map"
            data-testid="day-map-close-fullscreen"
          >
            <X size={24} />
          </button>
        )}

        {!fullscreen && !collapsed && (
          <button
            type="button"
            onClick={() => setFullscreen(true)}
            className="absolute top-2 right-2 z-[1000] bg-white p-2 rounded-lg shadow-lg hover:bg-zinc-100 transition-colors"
            title="Fullscreen map"
            data-testid="day-map-fullscreen-btn"
          >
            <Maximize2 size={18} />
          </button>
        )}

        {fullscreen && (
          <div className="absolute bottom-6 left-4 right-4 z-[1000] flex items-center justify-between">
            <div className="bg-white rounded-xl shadow-lg px-4 py-2">
              <span className="font-semibold text-zinc-800">{totalCount} jobs</span>
              {dayLabel && <span className="text-zinc-400 mx-2">•</span>}
              {dayLabel && <span className="text-zinc-600">{dayLabel}</span>}
              <span className="text-zinc-400 mx-2">•</span>
              <span className="text-emerald-600">{doneCount} done</span>
            </div>
          </div>
        )}

        {!collapsed && validJobs.length === 0 && mapLoaded && (() => {
          // Distinguish between THREE empty states so the message is accurate:
          //   1. No jobs at all today → "No jobs scheduled"
          //   2. All jobs done/skipped → "All jobs done — nothing left on the map"
          //   3. Jobs exist but none have coords → offer the geocoding button
          const totalJobs = jobs.length;
          const allFinished = totalJobs > 0 && jobs.every((j) =>
            j.status === 'skipped' || j.status === 'done' || j.status === 'completed'
          );
          const anyUngeocoded = jobs.some(
            (j) => !(j.latitude && j.longitude) && j.status !== 'completed' && j.status !== 'done' && j.status !== 'skipped'
          );
          
          if (allFinished) {
            return (
              <div className="absolute inset-0 bg-zinc-100/90 flex flex-col items-center justify-center p-4">
                <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mb-2">
                  <MapPin size={28} className="text-emerald-600" />
                </div>
                <p className="text-zinc-700 text-sm font-semibold">All {totalJobs} job{totalJobs !== 1 ? 's' : ''} done</p>
                <p className="text-zinc-500 text-xs mt-1">Nothing left on the map for {dayLabel || 'this day'}</p>
              </div>
            );
          }
          
          if (totalJobs === 0) {
            return (
              <div className="absolute inset-0 bg-zinc-100/90 flex flex-col items-center justify-center p-4">
                <MapPin size={32} className="text-zinc-400 mb-2" />
                <p className="text-zinc-500 text-sm">No jobs scheduled for {dayLabel || 'this day'}</p>
              </div>
            );
          }
          
          // anyUngeocoded → offer the batch-geocode button
          return (
            <div className="absolute inset-0 bg-zinc-100/90 flex flex-col items-center justify-center p-4">
              <MapPin size={32} className="text-zinc-400 mb-2" />
              <p className="text-zinc-500 text-sm mb-2">No map locations for {dayLabel || 'this day'}</p>
              <p className="text-zinc-400 text-xs mb-3 text-center max-w-xs">
                {anyUngeocoded
                  ? "Your customers don't have geo-coordinates yet (this happens after a fresh CSV import). Run a one-off batch geocode to put them on the map."
                  : "Customers have geo-coordinates but no jobs to pin today."}
              </p>
              {anyUngeocoded && (
                <button
                  onClick={async () => {
                    if (geocoding) return;
                    setGeocoding(true);
                    setGeocodingMsg('');
                    try {
                      const token = localStorage.getItem('streakfree_token');
                      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/geocode/customers/batch`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${token}` },
                      });
                      const data = await res.json();
                      if (res.ok) {
                        setGeocodingMsg(
                          typeof data.pending === 'number' && data.pending > 0
                            ? `Geocoding ${data.pending} customers in the background — refresh in a minute or two`
                            : (data.message || 'Geocoding started')
                        );
                      } else {
                        setGeocodingMsg(data.detail || 'Failed to start geocoding');
                      }
                    } catch (e) {
                      setGeocodingMsg('Network error');
                    } finally {
                      setGeocoding(false);
                    }
                  }}
                  disabled={geocoding}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-zinc-300 disabled:cursor-not-allowed rounded-lg text-sm font-medium text-white transition-colors"
                  data-testid="bulk-geocode-btn"
                >
                  {geocoding ? 'Starting…' : 'Geocode my customers'}
                </button>
              )}
              {geocodingMsg && (
                <p className={`text-xs mt-3 text-center max-w-xs ${geocodingMsg.startsWith('Failed') || geocodingMsg.startsWith('Network') ? 'text-red-600' : 'text-emerald-700'}`} data-testid="bulk-geocode-msg">
                  {geocodingMsg}
                </p>
              )}
            </div>
          );
        })()}
      </div>

      {/* Toggle bar */}
      {!fullscreen && (
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          className="w-full flex items-center justify-center gap-2 bg-white border-b border-zinc-200 py-2 px-4 text-sm font-medium text-zinc-600 hover:bg-zinc-50 transition-colors"
          data-testid="day-map-toggle-btn"
        >
          <MapPin size={16} className="text-emerald-500" />
          {collapsed ? 'Show Map' : 'Hide Map'}
          {!collapsed && validJobs.length > 0 && (
            <span className="text-zinc-400 text-xs">({validJobs.length} pinned)</span>
          )}
        </button>
      )}
    </div>
  );
};

export default DayMapPanel;
